import React, { useEffect, useState, useCallback } from 'lib/react';
import { EVPaper, EVGrid, makeStyles } from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import useMediaQuery from 'hooks/useMediaQuery';
import OrdersList from 'components/orders-list';
import Banner from 'commonComponents/banner';
import { isEntitledForEvOneIngredient } from 'utils/auth.utils';
import { INGREDIENTS } from 'constants/AppConstants';
import { isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import {
  closeOrderHistoryAlert, loadOrderHistoryPage,
} from './OrderHistory.actions';
import { PAGINATION_BREAKPOINT } from './OrderHistory.constants';
import { getInitialExpandedRowFlags, getOrderHistoryColumns } from './OrderHistory.utils';
import {
  CancelOrderAlert,
  CombinedExpandedSection,
  FiltersRow,
  NoOrdersScreen,
  PageOptions,
  RetryScreen,
  SearchRow,
} from './components';

const useStyles = makeStyles((theme) => ({
  orderHistory: ({ isSubscriptionDashboardV2Enabled }) => ({
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    columnGap: 0,
    '@media (min-width: 640px)': {
      margin: isSubscriptionDashboardV2Enabled ? '0' : '16px 24px',
      backgroundColor: theme.evPalette.salt[100],
    },
  }),
  ordersListContainer: ({ isSubscriptionDashboardV2Enabled }) => ({
    backgroundColor: theme.evPalette.charcoal[100],
    '@media (min-width: 640px)': {
      padding: isSubscriptionDashboardV2Enabled ? '16px 24px' : '0',
    },
  }),
}));

function OrderHistory() {
  const {
    loading, reportDetails, reportIds, searchText, loadError, orderHistoryAlert = {}, propertyDataMap, loadingPropertyData,
  } = useSelector(({ orderHistory }) => orderHistory);
  const userEntitlement = useSelector(({ userEntitlements }) => userEntitlements);
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);

  const mainPropertyDataMap = Object.entries(propertyDataMap).reduce((acc, [reportId, propertyData]) => {
    if (propertyData) {
      const { RoofCondition, FencePresent, ...filteredPropertyData } = propertyData;
      return { ...acc, [reportId]: filteredPropertyData };
    }
    return acc;
  }, {});

  const classes = useStyles({ isSubscriptionDashboardV2Enabled });
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(640);

  const [showFilters, setShowFilters] = useState(false);
  const [expandedRowFlags, setExpandedRowFlags] = useState({});

  // Check if user has Property Data SKU
  const hasPropertyDataSKU = isEntitledForEvOneIngredient(userEntitlement, INGREDIENTS.PROPERTY_DATA_PRE_INSPECTION);

  useEffect(() => {
    dispatch(loadOrderHistoryPage());
  }, []);

  useEffect(() => {
    const initialFlags = getInitialExpandedRowFlags(reportDetails, searchText);

    // If user has Property Data SKU, expand all rows by default
    if (hasPropertyDataSKU && reportDetails) {
      const propertyDataExpandedFlags = reportDetails.reduce((acc, { reportId }) => ({
        ...acc,
        [reportId]: false,
      }), {});

      setExpandedRowFlags({ ...initialFlags, ...propertyDataExpandedFlags });
    } else {
      setExpandedRowFlags(initialFlags);
    }
  }, [searchText, reportDetails, hasPropertyDataSKU]);

  const handleAlertClose = () => dispatch(closeOrderHistoryAlert());
  const handleExpandedRowVisibility = (key) => setExpandedRowFlags((prevState) => ({ ...prevState, [key]: !prevState[key] }));

  // Memoized component to avoid creating new components on every render
  const MemoizedExpandedRowComponent = useCallback((props) => (
    <CombinedExpandedSection
      {...props}
      propertyDataMap={mainPropertyDataMap || {}}
      loadingPropertyData={loadingPropertyData || false}
    />
  ), [propertyDataMap, loadingPropertyData]);

  if (!loading && loadError) {
    return <RetryScreen />;
  }

  return (
    <>
      {orderHistoryAlert.open && (
        <Banner
          severity={orderHistoryAlert.severity}
          handleAlertClose={handleAlertClose}
          title={orderHistoryAlert.title}
          message={orderHistoryAlert.message}
        />
      )}
      <EVPaper square={isMobile} style={{ flex: 1 }} className={classes.orderHistory}>
        <CancelOrderAlert />
        <SearchRow loading={loading} showFilters={showFilters} setShowFilters={setShowFilters} />
        {((!isMobile) || showFilters) && <FiltersRow />}
        {(reportIds?.length === 0) ? <NoOrdersScreen /> : (
          <EVGrid className={classes.ordersListContainer}>
            <OrdersList
              getFields={getOrderHistoryColumns}
              expandedRowFlags={expandedRowFlags}
              onExpandedRowFlagsChange={handleExpandedRowVisibility}
              ExpandedRowComponent={MemoizedExpandedRowComponent}
              propertyDataMap={mainPropertyDataMap || {}}
            />
          </EVGrid>
        )}
        <PageOptions loading={loading} hide={!reportIds || reportIds.length < PAGINATION_BREAKPOINT} />
      </EVPaper>
    </>
  );
}

export default OrderHistory;
